import React, { useState, useEffect } from 'react';
import { useAppContext } from '../../context/AppContext';
import { Reward } from 'shared/types';
import { ImageService } from '../../services/imageService';
import { deleteReward, createReward, updateReward, uploadFamilyPicture, rotateFamilyPicture } from '../../services/apiService';
import { saveInBackground } from '../../utils/saveInBackground';
import { UnreadableImageError } from '../../utils/prepareImage';
import { StoredPicture } from '../kid/StoredPicture';
import ConfirmDeleteModal from '../ConfirmDeleteModal';
import { i18n } from '../../i18n/i18n';
import { useLanguage } from '../../context/LanguageContext';
import { showToast } from '../kid/Toast';
import { dateInputToProgressStartAtIso, isoToDateInputValue } from '../../utils/rewardDateHelpers';
import HandOverRewards from '../parent/HandOverRewards';
import HeaderImage from './HeaderImage';
import { EditorModal, useCloseGuard } from './EditorModal';
import { PocketMoneySettings } from '../pocket/PocketMoney';

/** Admin → Rewards: hand-over list, rewards, reward form and delete (split out of AdminPanel.tsx, 2026-10-12). */
const RewardsTab: React.FC = () => {
  const { current_user, current_family, refreshFamilyData } = useAppContext() as any;
  const { current_language } = useLanguage();
  const [rewardModal, setRewardModal] = useState<{ isOpen: boolean; reward: Reward | null; isCreating: boolean }>({
    isOpen: false,
    reward: null,
    isCreating: false
  });
  const [rewardProgressDate, setRewardProgressDate] = useState<string>('');
  const [removeRewardModal, setRemoveRewardModal] = useState<{ isOpen: boolean; rewardId: string; rewardTitle: string; error?: string; isDeleting?: boolean }>({
    isOpen: false, rewardId: '', rewardTitle: ''
  });
  const [tempRewardData, setTempRewardData] = useState<Partial<Reward>>({});
  const [pictureBusy, setPictureBusy] = useState(false);
  useEffect(() => {
    if (!rewardModal.isOpen) return;
    if (rewardModal.isCreating) {
      setRewardProgressDate(new Date().toISOString().slice(0, 10));
      return;
    }
    const r = rewardModal.reward;
    if (r?.progress_start_at) {
      setRewardProgressDate(isoToDateInputValue(r.progress_start_at));
    } else {
      setRewardProgressDate(new Date().toISOString().slice(0, 10));
    }
  }, [rewardModal.isOpen, rewardModal.isCreating, rewardModal.reward]);
  const handleConfirmDeleteReward = async () => {
    if (!removeRewardModal.rewardId) return;
    setRemoveRewardModal(prev => ({ ...prev, isDeleting: true, error: undefined }));
    try {
      await deleteReward(removeRewardModal.rewardId);
      await refreshFamilyData();
      setRemoveRewardModal({ isOpen: false, rewardId: '', rewardTitle: '' });
    } catch (error) {
      setRemoveRewardModal(prev => ({ ...prev, isDeleting: false, error: (error as any)?.message || 'Failed to delete reward.' }));
    }
  };

  const handleCreateReward = async () => {
    try {
      if (!tempRewardData.title?.trim()) {
        showToast({ message: i18n.t('admin_panel.err_reward_title'), tone: 'error' });
        return;
      }

      if (!tempRewardData.points_required || tempRewardData.points_required <= 0) {
        showToast({ message: i18n.t('admin_panel.err_reward_points'), tone: 'error' });
        return;
      }

      // Validate that individual rewards have a target_id
      if (tempRewardData.target_type === 'individual' && !tempRewardData.target_id) {
        showToast({ message: i18n.t('admin_panel.err_reward_member'), tone: 'error' });
        return;
      }

      const payload = {
        title: tempRewardData.title.trim(),
        description: tempRewardData.description?.trim(),
        points_required: tempRewardData.points_required,
        target_type: tempRewardData.target_type || 'individual',
        target_id: tempRewardData.target_type === 'individual' ? tempRewardData.target_id : undefined,
        header_image: tempRewardData.header_image,
        progress_start_at: dateInputToProgressStartAtIso(rewardProgressDate),
      };

      // Close at once; "Saving reward…" until it is saved (like tasks, 2026-10-13).
      setRewardModal({ isOpen: false, reward: null, isCreating: false });
      setTempRewardData({});
      void saveInBackground({
        run: async () => { await createReward(payload as any); await refreshFamilyData(); },
        saving: i18n.t('admin_panel.reward_saving'),
        saved: i18n.t('admin_panel.reward_saved'),
        failed: i18n.t('admin_panel.err_reward_create'),
        tryAgain: i18n.t('admin_panel.try_again'),
      });
    } catch (error) {
      console.error('Failed to create reward:', error);
      showToast({ message: i18n.t('admin_panel.err_reward_create'), tone: 'error' });
    }
  };

  const handleUpdateReward = async () => {
    try {
      if (!rewardModal.reward?.id) {
        showToast({ message: i18n.t('admin_panel.err_generic_missing'), tone: 'error' });
        return;
      }

      if (!tempRewardData.title?.trim()) {
        showToast({ message: i18n.t('admin_panel.err_reward_title'), tone: 'error' });
        return;
      }

      if (!tempRewardData.points_required || tempRewardData.points_required <= 0) {
        showToast({ message: i18n.t('admin_panel.err_reward_points'), tone: 'error' });
        return;
      }

      // Validate that individual rewards have a target_id
      if (tempRewardData.target_type === 'individual' && !tempRewardData.target_id) {
        showToast({ message: i18n.t('admin_panel.err_reward_member'), tone: 'error' });
        return;
      }

      const rewardId = rewardModal.reward.id;
      const updates = {
        title: tempRewardData.title.trim(),
        description: tempRewardData.description?.trim(),
        points_required: tempRewardData.points_required,
        target_type: tempRewardData.target_type || 'individual',
        target_id: tempRewardData.target_type === 'individual' ? tempRewardData.target_id : undefined,
        header_image: tempRewardData.header_image,
        ...(!rewardModal.reward.is_claimed
          ? { progress_start_at: dateInputToProgressStartAtIso(rewardProgressDate) }
          : {}),
      };

      setRewardModal({ isOpen: false, reward: null, isCreating: false });
      void saveInBackground({
        run: async () => { await updateReward(rewardId, updates as any); await refreshFamilyData(); },
        saving: i18n.t('admin_panel.reward_saving'),
        saved: i18n.t('admin_panel.reward_saved'),
        failed: i18n.t('admin_panel.err_reward_update'),
        tryAgain: i18n.t('admin_panel.try_again'),
      });
    } catch (error) {
      console.error('Failed to update reward:', error);
      showToast({ message: i18n.t('admin_panel.err_reward_update'), tone: 'error' });
    }
  };


  const closeRewardEditor = () => setRewardModal({ isOpen: false, reward: null, isCreating: false });
  // ✕ / Esc / tap outside (2026-10-17): asks to save only when something was changed.
  const rewardCloseGuard = useCloseGuard({
    open: rewardModal.isOpen,
    snapshot: { tempRewardData, rewardProgressDate },
    save: () => (rewardModal.isCreating ? handleCreateReward() : handleUpdateReward()),
    discard: closeRewardEditor,
    saveDisabled: pictureBusy,
  });

  if (!current_family || !current_user) return null;
  return (
    <>
    <div className="mb-8">
      <HandOverRewards className="mb-6" />
      <div className="flex flex-wrap justify-between items-center gap-3 mb-5">
        <h3 className="font-kid-heading text-2xl font-extrabold text-kid-text dark:text-kid-text-dark">{i18n.t('admin_panel.reward_management')}</h3>
        <button 
          className="inline-flex items-center justify-center gap-2 min-h-[48px] px-5 rounded-full font-bold text-base bg-kid-green hover:bg-kid-green-hover text-white shadow-[0_3px_0_0_#1F5C39] disabled:opacity-50"
          onClick={() => {
            setTempRewardData({
              target_type: 'individual'  // Set default target type so dropdown appears immediately
            });
            setRewardModal({ isOpen: true, reward: null, isCreating: true });
          }}
        >
          + {i18n.t('admin_panel.create_new_reward')}
        </button>
      </div>
      
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
        {current_family.rewards.map((reward: any) => (
          <div key={reward.id} className={`bg-kid-card dark:bg-kid-card-dark rounded-kid-card border-2 border-kid-border dark:border-kid-border-dark p-4 ${reward.is_claimed ? 'opacity-70' : ''}`}>
            {/* Header Image */}
            {reward.header_image && (
              <div className="w-full aspect-[16/9] overflow-hidden rounded-kid-tile mb-3 bg-kid-chip dark:bg-kid-chip-dark">
                <HeaderImage imageId={reward.header_image} />
              </div>
            )}
            <div className="flex justify-between items-center mb-2">
              <h5 className="text-lg font-semibold text-kid-text dark:text-kid-text-dark">{reward.title}</h5>
              <span className={`text-sm font-medium ${reward.is_claimed ? 'text-red-600 dark:text-red-400' : 'text-green-600 dark:text-green-400'}`}>
                {reward.is_claimed ? i18n.t('admin_panel.claimed') : i18n.t('admin_panel.available')}
              </span>
            </div>
            <p className="text-kid-text dark:text-kid-text-dark mb-3">{reward.description}</p>
            <div className="text-kid-text dark:text-kid-text-dark text-sm space-y-1">
              <p>Points Required: {reward.points_required}</p>
              <p>Target: {reward.target_type}</p>
              {reward.progress_start_at && (
                <p>
                  {i18n.t('admin_panel.reward_progress_start_label')}:{' '}
                  {new Date(reward.progress_start_at).toLocaleDateString(current_language || undefined)}
                </p>
              )}
            </div>
            <div className="flex gap-2 mt-4">
              <button 
                className="inline-flex items-center justify-center gap-2 min-h-[48px] px-5 rounded-full font-bold text-base bg-kid-card dark:bg-kid-card-dark border-2 border-kid-border-strong dark:border-kid-border-strong-dark text-kid-text dark:text-kid-text-dark disabled:opacity-50"
                onClick={() => {
                  // Map backend snake_case fields to frontend camelCase fields
                  setTempRewardData({
                    id: reward.id, // so a new or turned picture is filed under this reward
                    title: reward.title,
                    description: reward.description,
                    points_required: reward.points_required,
                    target_type: reward.target_type,
                    target_id: reward.target_id,
                    header_image: reward.header_image
                  });
                  setRewardModal({ isOpen: true, reward, isCreating: false });
                }}
              >
                {i18n.t('admin_panel.edit')}
              </button>
              <button
                className="inline-flex items-center justify-center gap-2 min-h-[48px] px-5 rounded-full font-bold text-base bg-transparent border-2 border-red-300 dark:border-red-800 text-red-800 dark:text-red-200 disabled:opacity-50"
                onClick={() => setRemoveRewardModal({ isOpen: true, rewardId: reward.id, rewardTitle: reward.title })}
              >
                {i18n.t('admin_panel.delete_btn')}
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
      {/* Pocket money per child (2026-10-14) */}
      <PocketMoneySettings />
      {/* Reward Modal */}
      {rewardModal.isOpen && (
        <EditorModal
          onRequestClose={rewardCloseGuard.requestClose}
          title={rewardModal.isCreating ? i18n.t('admin_panel.reward_modal_title_create') : `${i18n.t('admin_panel.reward_modal_title_edit_prefix')}: ${rewardModal.reward?.title}`}
        >
            <div className="p-6 space-y-6">
              <div className="space-y-4">
                <div className="w-full max-w-md">
                  <label className="block text-base font-bold mb-1">{i18n.t('admin_panel.reward_title_label')}</label>
                  <input 
                    type="text" 
                    placeholder={i18n.t('admin_panel.reward_title_placeholder')}
                    value={tempRewardData.title || ''}
                    onChange={(e) => setTempRewardData({ ...tempRewardData, title: e.target.value })} 
                    className="w-full min-h-[48px] rounded-kid-tile border-2 border-kid-border-strong dark:border-kid-border-strong-dark bg-kid-card dark:bg-kid-card-dark text-kid-text dark:text-kid-text-dark px-3 py-2 text-base focus:outline-none focus:border-kid-green" 
                  />
                </div>
                
                <div className="w-full max-w-md">
                  <label className="block text-base font-bold mb-1">{i18n.t('admin_panel.reward_desc_label')}</label>
                  <textarea 
                    placeholder={i18n.t('admin_panel.reward_desc_placeholder')}
                    value={tempRewardData.description || ''}
                    onChange={(e) => setTempRewardData({ ...tempRewardData, description: e.target.value })} 
                    className="w-full min-h-[48px] rounded-kid-tile border-2 border-kid-border-strong dark:border-kid-border-strong-dark bg-kid-card dark:bg-kid-card-dark text-kid-text dark:text-kid-text-dark px-3 py-2 text-base focus:outline-none focus:border-kid-green" 
                    rows={3}
                  />
                </div>
                
                <div className="w-full max-w-md">
                  <label className="block text-base font-bold mb-1">{i18n.t('admin_panel.points_required_label')}</label>
                  <input 
                    type="number" 
                    placeholder={i18n.t('library.stars')} 
                    value={tempRewardData.points_required || 0}
                    onChange={(e) => setTempRewardData({ ...tempRewardData, points_required: parseInt(e.target.value) || 0 })} 
                    className="w-full min-h-[48px] rounded-kid-tile border-2 border-kid-border-strong dark:border-kid-border-strong-dark bg-kid-card dark:bg-kid-card-dark text-kid-text dark:text-kid-text-dark px-3 py-2 text-base focus:outline-none focus:border-kid-green" 
                    min="1" 
                  />
                </div>

                <div className="w-full max-w-md">
                  <label className="block text-base font-bold mb-1">{i18n.t('admin_panel.reward_progress_start_label')}</label>
                  <input
                    type="date"
                    value={rewardProgressDate}
                    onChange={(e) => setRewardProgressDate(e.target.value)}
                    disabled={!rewardModal.isCreating && !!rewardModal.reward?.is_claimed}
                    className="w-full px-3 py-2 border-2 border-kid-border-strong dark:border-kid-border-dark bg-kid-card dark:bg-kid-card-dark text-kid-text dark:text-kid-text-dark rounded-kid-tile focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:opacity-50"
                  />
                  <p className="text-xs text-kid-text-muted dark:text-kid-text-muted-dark mt-1">{i18n.t('admin_panel.reward_progress_start_hint')}</p>
                </div>
                
                <div className="w-full max-w-md">
                  <label className="block text-base font-bold mb-1">{i18n.t('admin_panel.target_type_label')}</label>
                  <select 
                    value={tempRewardData.target_type || 'individual'}
                    onChange={(e) => setTempRewardData({ ...tempRewardData, target_type: e.target.value as 'individual' | 'family' })} 
                    className="w-full min-h-[48px] rounded-kid-tile border-2 border-kid-border-strong dark:border-kid-border-strong-dark bg-kid-card dark:bg-kid-card-dark text-kid-text dark:text-kid-text-dark px-3 py-2 text-base focus:outline-none focus:border-kid-green"
                  >
                    <option value="individual">{i18n.t('admin_panel.target_type_individual')}</option>
                    <option value="family">{i18n.t('admin_panel.target_type_family')}</option>
                  </select>
                </div>
                
                {/* Family Member Selection - Only show when Individual is selected */}
                {tempRewardData.target_type === 'individual' && (
                  <div className="w-full max-w-md">
                    <label className="block text-base font-bold mb-1">{i18n.t('admin_panel.assign_member_label')}</label>
                    <select 
                      value={tempRewardData.target_id || ''}
                      onChange={(e) => setTempRewardData({ ...tempRewardData, target_id: e.target.value })} 
                      className="w-full min-h-[48px] rounded-kid-tile border-2 border-kid-border-strong dark:border-kid-border-strong-dark bg-kid-card dark:bg-kid-card-dark text-kid-text dark:text-kid-text-dark px-3 py-2 text-base focus:outline-none focus:border-kid-green"
                    >
                      <option value="">{i18n.t('admin_panel.select_member_option')}</option>
                      {current_family?.members.map((member: any) => (
                        <option key={member.id} value={member.id}>
                          {member.name}
                        </option>
                      ))}
                    </select>
                  </div>
                )}
                
                <div className="w-full max-w-md">
                  <label className="block text-base font-bold mb-1">{i18n.t('admin_panel.header_image_label')}</label>
                  <input 
                    type="file" 
                    accept="image/*" 
                    onChange={async (e) => {
                      // Uploaded (HEIC → JPEG, shrunk) so every family device sees it (2026-10-13).
                      const f = e.target.files?.[0];
                      e.target.value = '';
                      if (!f) return;
                      setPictureBusy(true);
                      try {
                        const url = await uploadFamilyPicture('reward_header', f, { reward_id: (tempRewardData as any).id });
                        setTempRewardData((prev) => ({ ...prev, header_image: url }));
                      } catch (error) {
                        showToast({ message: i18n.t(error instanceof UnreadableImageError ? 'pictures.unreadable' : 'admin_panel.err_header_image'), tone: 'error' });
                      } finally {
                        setPictureBusy(false);
                      }
                    }} 
                    className="w-full min-h-[48px] rounded-kid-tile border-2 border-kid-border-strong dark:border-kid-border-strong-dark bg-kid-card dark:bg-kid-card-dark text-kid-text dark:text-kid-text-dark px-3 py-2 text-base focus:outline-none focus:border-kid-green" 
                  />
                  {tempRewardData.header_image && (
                    <div className="mt-2">
                      <div className="mb-2">
                        <StoredPicture imageId={tempRewardData.header_image} label={tempRewardData.title || ''} size={96} />
                      </div>
                      <button
                        type="button"
                        disabled={pictureBusy}
                        onClick={async () => {
                          // ↻ a quarter turn clockwise; the turned copy replaces the picture.
                          const current = tempRewardData.header_image;
                          if (!current) return;
                          setPictureBusy(true);
                          try {
                            const url = await rotateFamilyPicture('reward_header', current, { reward_id: (tempRewardData as any).id });
                            setTempRewardData((prev) => ({ ...prev, header_image: url }));
                          } catch (error) {
                            showToast({ message: i18n.t(error instanceof UnreadableImageError ? 'pictures.unreadable' : 'admin_panel.err_header_image'), tone: 'error' });
                          } finally {
                            setPictureBusy(false);
                          }
                        }}
                        className="inline-flex items-center justify-center gap-2 min-h-[48px] px-5 mr-2 rounded-full font-bold text-base border-2 border-kid-border-strong dark:border-kid-border-strong-dark disabled:opacity-50"
                      >
                        <span aria-hidden>↻</span> {i18n.t('pictures.rotate')}
                      </button>
                      <button 
                        onClick={() => setTempRewardData({ ...tempRewardData, header_image: undefined })} 
                        className="inline-flex items-center justify-center gap-2 min-h-[48px] px-5 rounded-full font-bold text-base bg-transparent border-2 border-red-300 dark:border-red-800 text-red-800 dark:text-red-200 disabled:opacity-50"
                      >
                        {i18n.t('common.remove_image') || 'Remove Image'}
                      </button>
                    </div>
                  )}
                </div>
              </div>
              
              <div className="flex gap-3 pt-5 border-t-2 border-kid-border dark:border-kid-border-dark">
                <button onClick={closeRewardEditor} className="inline-flex items-center justify-center gap-2 min-h-[48px] px-5 rounded-full font-bold text-base bg-kid-card dark:bg-kid-card-dark border-2 border-kid-border-strong dark:border-kid-border-strong-dark text-kid-text dark:text-kid-text-dark disabled:opacity-50 flex-1">{i18n.t('admin_panel.cancel_btn')}</button>
                {rewardModal.isCreating ? (
                  <button onClick={handleCreateReward} disabled={pictureBusy} className="inline-flex items-center justify-center gap-2 min-h-[48px] px-5 rounded-full font-bold text-base bg-kid-green hover:bg-kid-green-hover text-white shadow-[0_3px_0_0_#1F5C39] disabled:opacity-50 flex-1">{i18n.t('admin_panel.create_reward_btn')}</button>
                ) : (
                  <button onClick={handleUpdateReward} disabled={pictureBusy} className="inline-flex items-center justify-center gap-2 min-h-[48px] px-5 rounded-full font-bold text-base bg-kid-green hover:bg-kid-green-hover text-white shadow-[0_3px_0_0_#1F5C39] disabled:opacity-50 flex-1">{i18n.t('admin_panel.save_changes_btn')}</button>
                )}
              </div>
            </div>
          {rewardCloseGuard.dialog}
        </EditorModal>
      )}
      <ConfirmDeleteModal
        isOpen={removeRewardModal.isOpen}
        title={i18n.t('admin_panel.delete_reward_title')}
        message={<>{i18n.t('admin_panel.delete_confirm_prefix')} "<strong>{removeRewardModal.rewardTitle}</strong>"?</>}
        onConfirm={handleConfirmDeleteReward}
        onCancel={() => setRemoveRewardModal({ isOpen: false, rewardId: '', rewardTitle: '' })}
        isDeleting={removeRewardModal.isDeleting}
        error={removeRewardModal.error}
      />
    </>
  );
};

export default RewardsTab;
